import React, {useCallback, useEffect, useRef, useState} from "react";
import {
  ApiError,
  formatMerchantApiError,
  getMerchantOrder,
  getMerchantOrderReceipts,
  getMerchantReceipt,
  getMerchantOrderTimeline,
  getCurrentPortalRequisites,
} from "../../api";
import type {
  MerchantOrderDetails,
  MerchantReceiptItem,
  MerchantTimelineResponse,
} from "../../api";
import {useAuth} from "../../auth";
import {MerchantConversion, MerchantStatusBadge, MerchantTerms} from "../../components";
import {MerchantCancellation} from "../../components/MerchantCancellation";
import {PortalReceiptList} from "../../components/PortalReceiptList";
import {PayinParts, PayinStageBadge} from "../../components/PayinParts";
import {MerchantReceiptDrafts} from "../../components/MerchantReceiptDrafts";
import {CopyButton} from "../../components/CopyButton";
import {withReceiptExtension} from "../../components/payment-details";
import {useMerchantQuery} from "../../hooks";
import {
  formatMerchantDate,
  merchantAmountText,
  merchantDirectionLabel,
  merchantOrderPartsLabel,
  merchantOrderStatusLabel,
  merchantTimelineLabel,
} from "../../utils";
import {
  merchantReceiptFilename,
  saveMerchantBlob,
} from "../../utils/merchantDownloads";

interface MerchantOrderDetailsPageProps {
  orderId: string;
}

interface MerchantOrderPageData {
  details: MerchantOrderDetails;
  timeline: MerchantTimelineResponse;
}

/**
 * receiptAmountLabel — сумма показывается один раз на часть заявки.
 *
 * Трейдер может закрыть свою часть несколькими переводами и приложить к ней
 * несколько чеков (В-23). Сумма в строке — сумма всей части, и если повторить
 * её у каждого чека, три чека одной части на 5 000 ₽ прочитаются как 15 000 ₽.
 * Поэтому у второго и следующих чеков части вместо суммы стоит отсылка к
 * первому. Чек отмены суммы не несёт и к соседней части не относится.
 */
function receiptAmountLabel(
  receipts: MerchantReceiptItem[],
  index: number,
): string {
  const receipt = receipts[index];
  if (receipt.cancellation) {
    return "чек к отмене · ";
  }
  const previous = index > 0 ? receipts[index - 1] : null;
  if (previous && !previous.cancellation && previous.part === receipt.part) {
    return "та же часть · ";
  }
  return receipt.amount ? `${receipt.amount} ${receipt.currency} · ` : "";
}

export function MerchantOrderDetailsPage({
  orderId,
}: MerchantOrderDetailsPageProps): JSX.Element {
  const {token} = useAuth();
  const request = useCallback(
    async (signal: AbortSignal): Promise<MerchantOrderPageData> => {
      const [details, timeline] = await Promise.all([
        getMerchantOrder(token, orderId, signal),
        getMerchantOrderTimeline(token, orderId, signal),
      ]);
      return {details, timeline};
    },
    [orderId, token],
  );
  const query = useMerchantQuery(request, `merchant-order:${orderId}`, {
    enabled: Boolean(token && orderId),
  });
  const details = query.data?.details ?? null;
  const [currentRequisites, setCurrentRequisites] = useState<import("../../api").PortalCurrentRequisites | null>(null);
  const [portalReceiptRefresh, setPortalReceiptRefresh] = useState(0);
  const [requisitesError, setRequisitesError] = useState(false);
  const orderSide = details?.side;
  const orderUpdatedAt = details?.updatedAt;
  useEffect(() => { setCurrentRequisites(null); setRequisitesError(false); }, [orderId, token]);
  useEffect(() => {
    if (orderSide !== "in" || !token) { setCurrentRequisites(null); return; }
    const controller = new AbortController();
    let active = true;
    let requestSerial = 0;
    const loadCurrent = (signal?: AbortSignal): void => {
      const serial = ++requestSerial;
      void getCurrentPortalRequisites(token, orderId, signal).then((value) => {
        if (active && serial === requestSerial) { setCurrentRequisites(value); setRequisitesError(false); }
      }).catch((error) => {
        if (!active || serial !== requestSerial || signal?.aborted) return;
        if (error instanceof ApiError && [401, 403, 404].includes(error.status)) {
          setCurrentRequisites(null); setRequisitesError(false);
        } else {
          // Keep unsent files mounted during a transient read failure.
          setRequisitesError(true);
        }
      });
    };
    loadCurrent(controller.signal);
    const timer = window.setInterval(() => {
      loadCurrent();
    }, 30_000);
    return () => { active = false; controller.abort(); window.clearInterval(timer); };
  }, [orderSide, orderUpdatedAt, orderId, token, portalReceiptRefresh]);
  const receipts = details?.receipts ?? [];
  const receiptRequestRef = useRef<AbortController | null>(null);
  const receiptURLRef = useRef<string | null>(null);
  const [receiptLoading, setReceiptLoading] = useState<string | null>(null);
  const [receiptError, setReceiptError] = useState<string | null>(null);
  const [receiptViewer, setReceiptViewer] = useState<{
    url: string;
    index: number;
    // Тип пришедшего файла: чек бывает и PDF, и скриншотом, а имя для скачивания
    // собирается здесь.
    mimeType: string;
  } | null>(null);
  const orderReceiptsRequestRef = useRef<AbortController | null>(null);
  const [orderReceiptsLoading, setOrderReceiptsLoading] = useState(false);
  const [orderReceiptsError, setOrderReceiptsError] = useState<string | null>(null);

  const releaseReceiptURL = useCallback((): void => {
    if (receiptURLRef.current) {
      URL.revokeObjectURL(receiptURLRef.current);
      receiptURLRef.current = null;
    }
  }, []);

  const closeReceipt = useCallback((): void => {
    receiptRequestRef.current?.abort();
    receiptRequestRef.current = null;
    setReceiptLoading(null);
    releaseReceiptURL();
    setReceiptViewer(null);
  }, [releaseReceiptURL]);

  const openReceipt = useCallback(async (
    receipt: MerchantReceiptItem,
    index: number,
  ): Promise<void> => {
    receiptRequestRef.current?.abort();
    const controller = new AbortController();
    receiptRequestRef.current = controller;
    setReceiptLoading(receipt.receiptId);
    setReceiptError(null);
    try {
      const blob = await getMerchantReceipt(
        token,
        receipt.receiptId,
        controller.signal,
      );
      releaseReceiptURL();
      const url = URL.createObjectURL(blob);
      receiptURLRef.current = url;
      setReceiptViewer({
        url,
        index,
        mimeType: blob.type,
      });
    } catch (error) {
      if (!(error instanceof DOMException && error.name === "AbortError")) {
        setReceiptError(formatMerchantApiError(error));
      }
    } finally {
      if (receiptRequestRef.current === controller) {
        receiptRequestRef.current = null;
        setReceiptLoading(null);
      }
    }
  }, [releaseReceiptURL, token]);

  const downloadOrderReceipts = useCallback(async (): Promise<void> => {
    if (!details || receipts.length === 0) return;
    orderReceiptsRequestRef.current?.abort();
    const controller = new AbortController();
    orderReceiptsRequestRef.current = controller;
    setOrderReceiptsLoading(true);
    setOrderReceiptsError(null);
    try {
      const blob = await getMerchantOrderReceipts(
        token,
        details.orderId,
        controller.signal,
      );
      saveMerchantBlob(
        blob,
        merchantReceiptFilename(details.orderId, blob),
      );
    } catch (error) {
      if (!(error instanceof DOMException && error.name === "AbortError")) {
        setOrderReceiptsError(formatMerchantApiError(error));
      }
    } finally {
      if (orderReceiptsRequestRef.current === controller) {
        orderReceiptsRequestRef.current = null;
        setOrderReceiptsLoading(false);
      }
    }
  }, [details, receipts.length, token]);

  useEffect(() => () => {
    receiptRequestRef.current?.abort();
    orderReceiptsRequestRef.current?.abort();
    releaseReceiptURL();
  }, [releaseReceiptURL]);

  const viewedReceipt = receiptViewer
    ? receipts[receiptViewer.index] ?? null
    : null;
  const receiptTitle = receiptViewer
    ? `Чек ${receiptViewer.index + 1} из ${receipts.length}`
    : "";
  const receiptCaption = viewedReceipt?.amount
    ? `${viewedReceipt.amount} ${viewedReceipt.currency} · ${formatMerchantDate(viewedReceipt.createdAt)}`
    : viewedReceipt
      ? formatMerchantDate(viewedReceipt.createdAt)
      : "";
  // Оплачено и остаток имеют смысл только после первого поступления: до него
  // они повторяют сумму заявки и ноль. К оплате по текущим реквизитам — остаток
  // заявки, а до первой оплаты — вся сумма.
  const paid = Number(details?.paidAmount ?? 0) > 0;
  const payAmount = details ? (paid ? details.remainingAmount : details.amount) : "";
  const orderOpen = Boolean(details && details.status !== "OK" && details.status !== "CANCEL");
  const payTarget = currentRequisites
    ? currentRequisites.kind === "sbp" ? currentRequisites.phone : currentRequisites.cardNumber
    : undefined;

  return (
    <div className="page merchant-page merchant-order-details-page">
      <header className="page-header merchant-page-header">
        <div>
          <a className="merchant-back-link" href="#/merchant/orders">
            ← К заявкам
          </a>
          <h1>{details ? `Заявка ${details.remoteId}` : "Детали заявки"}</h1>
        </div>
        <button
          className="btn btn-ghost"
          type="button"
          onClick={query.refresh}
          disabled={query.loading || query.refreshing}
        >
          {query.refreshing ? "Обновление…" : "Обновить"}
        </button>
      </header>

      {query.stale && query.error && (
        <div className="alert alert-warning merchant-stale-banner" role="status">
          {query.error} Показаны последние успешные данные.
          <button className="btn-link-clear" type="button" onClick={query.refresh}>
            Повторить
          </button>
        </div>
      )}

      {query.loading && !query.data && (
        <div className="merchant-skeleton" aria-label="Загрузка заявки" aria-busy="true">
          <div className="merchant-skeleton-panel"/>
          <div className="merchant-skeleton-panel"/>
        </div>
      )}

      {!query.loading && !query.data && query.error && (
        <section className="merchant-error-state" role="alert">
          <h2>Заявка недоступна</h2>
          <p>{query.error}</p>
          <div className="merchant-error-actions">
            <a className="btn btn-ghost" href="#/merchant/orders">К списку</a>
            <button className="btn btn-primary" type="button" onClick={query.refresh}>
              Повторить
            </button>
          </div>
        </section>
      )}

      {details && query.data && (
        <>
          <section className="merchant-detail-hero">
            <div className="merchant-detail-status">
              <span className="meta-label">Статус</span>
              {/* Статус заявки идёт первым: этап выдачи реквизитов — уточнение
                  к нему, а не замена. */}
              <MerchantStatusBadge
                status={details.status}
                label={merchantOrderStatusLabel(details)}
              />
              {details.payinStage && details.status !== "OK" && details.status !== "CANCEL" && <PayinStageBadge issued={details.payinStage === "requisites_issued"}/>}
            </div>
            <div>
              <span className="meta-label">Сумма</span>
              <strong className="mono merchant-money-stack">
                <span>{merchantAmountText(details)}</span>
                <MerchantTerms item={details}/>
                {details.conversion && (
                  <MerchantConversion conversion={details.conversion}/>
                )}
              </strong>
            </div>
            <div>
              <span className="meta-label">Направление</span>
              <strong>{merchantDirectionLabel(details.side)}</strong>
            </div>
            <div>
              <span className="meta-label">Платежей</span>
              <strong className="mono">{merchantOrderPartsLabel(details)}</strong>
            </div>
          </section>

          {requisitesError && <div className="alert alert-warning" role="alert">Не удалось обновить реквизиты. Выбранные чеки сохранены в этой вкладке; отправка временно недоступна. <button className="btn btn-ghost" onClick={() => setPortalReceiptRefresh((value) => value + 1)}>Обновить реквизиты</button></div>}
          {details.side === "in" && currentRequisites?.parts?.length ? <PayinParts
              orderId={orderId} state={{...currentRequisites, parts: currentRequisites.parts}} audience="merchant"
              active={details.status !== "OK" && details.status !== "CANCEL"}
              disabled={requisitesError || query.stale}
              onChanged={async () => {setPortalReceiptRefresh((value) => value + 1); await query.refresh();}}
          /> : details.side === "in" && currentRequisites && (
            <section className="merchant-section-card merchant-pay-section" aria-labelledby="merchant-pay-title">
              <div className="receipt-upload-panel">
                <div className="merchant-receipt-upload-header">
                  <h2 id="merchant-pay-title">{orderOpen ? "Реквизиты для оплаты" : "Реквизиты оплаты"}</h2>
                  {orderOpen && (
                    <p className="merchant-pay-amount">
                      <span>К оплате</span>
                      <strong className="mono">{payAmount} {details.currency}</strong>
                      <CopyButton value={payAmount} label="сумму" disabled={requisitesError || query.stale}/>
                    </p>
                  )}
                </div>
                <dl className="merchant-current-requisites">
                  <Detail
                    label={currentRequisites.kind === "sbp" ? "Телефон СБП" : "Номер карты"}
                    value={payTarget ? (
                      <span className="merchant-copy-value">
                        <span className="mono">{payTarget}</span>
                        {orderOpen && <CopyButton value={payTarget.replace(/\s+/g, "")} label={currentRequisites.kind === "sbp" ? "телефон" : "номер карты"} disabled={requisitesError || query.stale}/>}
                      </span>
                    ) : "—"}
                  />
                  <Detail label="Банк" value={currentRequisites.bankName}/>
                  <Detail
                    label="Получатель"
                    value={currentRequisites.kind === "sbp"
                      ? currentRequisites.recipient ?? "—"
                      : currentRequisites.cardHolder ?? "—"}
                  />
                </dl>
                <MerchantReceiptDrafts orderId={orderId} target={currentRequisites} disabled={requisitesError || query.stale || details.status === "OK" || details.status === "CANCEL"} onChanged={async () => {setPortalReceiptRefresh((value) => value + 1); await query.refresh();}}/>
              </div>
            </section>
          )}

          {receipts.length > 0 && <section className="merchant-section-card" aria-labelledby="merchant-receipts-title">
            <div className="section-header merchant-section-header">
              <div>
                <h2 id="merchant-receipts-title">Чеки</h2>
                <span>Актуальные чеки по платежам этой заявки</span>
              </div>
              <button
                className="btn btn-primary"
                type="button"
                onClick={() => void downloadOrderReceipts()}
                disabled={orderReceiptsLoading}
              >
                {orderReceiptsLoading
                  ? "Скачиваем…"
                  : receipts.length > 1
                    ? "Скачать чеки"
                    : "Скачать чек"}
              </button>
            </div>
            {orderReceiptsError && (
              <div className="alert alert-warning" role="alert">
                {orderReceiptsError}
              </div>
            )}
            {receiptError && (
              <div className="alert alert-warning" role="alert">
                {receiptError}
              </div>
            )}
            <ul className="merchant-receipt-list">
              {receipts.map((receipt, index) => (
                <li key={receipt.receiptId}>
                  <div>
                    <strong>Чек {index + 1}</strong>
                    <span>
                      {receiptAmountLabel(receipts, index)}
                      {formatMerchantDate(receipt.createdAt)}
                    </span>
                  </div>
                  <button
                    className="btn btn-ghost"
                    type="button"
                    onClick={() => void openReceipt(receipt, index)}
                    disabled={receiptLoading === receipt.receiptId}
                  >
                    {receiptLoading === receipt.receiptId
                      ? "Открываем…"
                      : "Посмотреть чек"}
                  </button>
                </li>
              ))}
            </ul>
          </section>}

          <section className="merchant-section-card">
            <div className="section-header merchant-section-header">
              <div>
                <h2>Сведения о заявке</h2>
              </div>
            </div>
            {/* Статус и сумма — в шапке заявки, реквизиты — в своём блоке
                выше; здесь остаётся только то, чего там нет. Пустые поля не
                выводятся: строка с прочерком ничего не сообщает. */}
            <dl className="merchant-details-grid">
              <Detail label="Внешний ID" value={details.remoteId} mono/>
              {paid && <Detail label="Оплачено" value={`${details.paidAmount} ${details.currency}`} mono/>}
              {paid && <Detail label="Осталось" value={`${details.remainingAmount} ${details.currency}`} mono/>}
              <Detail label="Создана" value={formatMerchantDate(details.createdAt)}/>
              <Detail label="Обновлена" value={formatMerchantDate(details.updatedAt)}/>
              {details.clientCard && <Detail label="Карта клиента" value={details.clientCard} mono/>}
              {details.clientPhone && <Detail label="Телефон клиента" value={details.clientPhone} mono/>}
              {details.clientBank && <Detail label="Банк клиента" value={details.clientBank}/>}
              {details.clientName && <Detail label="Имя клиента" value={details.clientName}/>}
              {details.payinRequisites && !currentRequisites && (
                <Detail
                  label={details.payinRequisites.kind === "sbp" ? "СБП реквизиты" : "Карта трейдера (скрыта)"}
                  value={details.payinRequisites.kind === "sbp"
                    ? [details.payinRequisites.bankName, details.payinRequisites.phone, details.payinRequisites.recipient].filter(Boolean).join(" · ")
                    : `${details.payinRequisites.bankName} · ${details.payinRequisites.cardMask}`}
                  mono
                />
              )}
            </dl>
          </section>

          {details.side === "in" && !currentRequisites?.parts?.length && <>
            <MerchantCancellation key={orderId} orderId={orderId} refreshKey={details.updatedAt} onCancelled={() => {void query.refresh();}}/>
            <PortalReceiptList orderId={orderId} refreshKey={`${details.updatedAt}:${portalReceiptRefresh}`} />
          </>}


          <section className="merchant-section-card">
            <div className="section-header merchant-section-header">
              <div>
                <h2>История заявки</h2>
                <span>Пользовательские события в хронологическом порядке</span>
              </div>
            </div>
            {query.data.timeline.items.length === 0 ? (
              <div className="empty-state compact">Событий пока нет.</div>
            ) : (
              <ol className="merchant-timeline">
                {query.data.timeline.items.map((event, index) => (
                  <li key={`${event.occurredAt}-${index}`}>
                    <div className="merchant-timeline-marker" aria-hidden="true"/>
                    <div className="merchant-timeline-content">
                      <div className="merchant-timeline-head">
                        <strong>{merchantTimelineLabel(event.kind)}</strong>
                        <time dateTime={event.occurredAt}>
                          {formatMerchantDate(event.occurredAt)}
                        </time>
                      </div>
                      <div className="merchant-timeline-meta">
                        <MerchantStatusBadge status={event.status}/>
                        {event.amount && event.currency && (
                          <span className="mono">{event.amount} {event.currency}</span>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </section>

          {receiptViewer && viewedReceipt && (
            <div
              className="merchant-receipt-dialog-backdrop"
              role="dialog"
              aria-modal="true"
              aria-labelledby="merchant-receipt-dialog-title"
            >
              <section className="merchant-receipt-dialog">
                <header>
                  <h2 id="merchant-receipt-dialog-title">
                    {receiptTitle}
                  </h2>
                  <button
                    className="btn btn-ghost"
                    type="button"
                    onClick={closeReceipt}
                    aria-label="Закрыть чек"
                  >
                    Закрыть
                  </button>
                </header>
                <p className="merchant-receipt-dialog-meta">
                  {receiptCaption}
                </p>
                <iframe
                  title={receiptTitle}
                  src={receiptViewer.url}
                  className="merchant-receipt-frame"
                />
                <footer className="merchant-receipt-dialog-footer">
                  <div
                    className="merchant-receipt-slider-controls"
                    aria-label="Переключение чеков"
                  >
                    <button
                      className="btn btn-ghost"
                      type="button"
                      onClick={() => {
                        const previousIndex = receiptViewer.index - 1;
                        void openReceipt(receipts[previousIndex], previousIndex);
                      }}
                      disabled={receiptViewer.index === 0 || receiptLoading !== null}
                      aria-label="Предыдущий чек"
                    >
                      ← Предыдущий
                    </button>
                    <span aria-live="polite">{receiptTitle}</span>
                    <button
                      className="btn btn-ghost"
                      type="button"
                      onClick={() => {
                        const nextIndex = receiptViewer.index + 1;
                        void openReceipt(receipts[nextIndex], nextIndex);
                      }}
                      disabled={receiptViewer.index === receipts.length - 1 || receiptLoading !== null}
                      aria-label="Следующий чек"
                    >
                      Следующий →
                    </button>
                  </div>
                  <a
                    className="btn btn-primary"
                    href={receiptViewer.url}
                    download={withReceiptExtension(
                      `receipt-${receiptViewer.index + 1}.pdf`,
                      receiptViewer.mimeType,
                    )}
                  >
                    Скачать чек
                  </a>
                </footer>
              </section>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Detail({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}): JSX.Element {
  return (
    <div>
      <dt>{label}</dt>
      <dd className={mono ? "mono" : undefined}>{value}</dd>
    </div>
  );
}
